"use client";

import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useQuery, useQueries } from "@tanstack/react-query";
import { Emoji } from "@/components/ui/Emoji";
import { EmojiGrid } from "./EmojiGrid";
import { EmojiPickerSheet } from "./EmojiPickerSheet";
import { Input } from "./ui/Input";
import { cn } from "./ui/cn";
import { useDismissable } from "@/lib/hooks/useDismissable";
import { computeAnchoredPosition, popoverBoundsRect } from "@/lib/anchoredPosition";
import { REACTION_EMOJI } from "@/lib/reactions";
import { getCustomEmojis } from "@/lib/api/emojis";
import { getEmojiFrequencies } from "@/lib/api/reactions";
import { useConversation } from "@/lib/hooks/useConversation";
import { otherParticipants } from "@/app/beskeder/participants";
import type { EmojiUse } from "@/lib/api/reactions";
import { customEmojisKey } from "@/lib/hooks/useGroupPage";

const MENU_HEIGHT = 360;
// The scrolling emoji grid's natural cap (`max-h-56`), and what MENU_HEIGHT
// budgets for it — the difference is the panel's fixed chrome above it (the
// frequently-used row and the search box), which is what has to be paid for
// before a single emoji is visible.
const GRID_HEIGHT = 224;
const PANEL_CHROME = MENU_HEIGHT - GRID_HEIGHT;
// Below this the grid is not worth rendering (roughly two rows), so a box too
// cramped for even that overflows deliberately rather than showing a sliver.
const MIN_GRID_HEIGHT = 96;
// The collapsed quick bar: one row of 24px emoji buttons plus padding.
const QUICK_BAR_HEIGHT = 56;
// `mb-3`/`mt-3` plus the little arrow: space the popover needs between the
// trigger and the panel itself.
const POPOVER_GAP = 19;
// Sized to still fit inside the chat dock (a 320px-wide floating window,
// see ChatDock.tsx) — the picker is also used there, not just in the much
// roomier post/comment/lightbox contexts, and the dock is the tightest fit.
const MENU_WIDTH = 240;

// Shown as the "frequently used" row until the caller has a real usage
// history — the same six sentiments the fixed reaction picker used to
// offer, so there's no empty-feeling first run.
const DEFAULT_QUICK_EMOJI: EmojiUse[] = Object.values(REACTION_EMOJI).map((emoji) => ({
  emoji,
  url: null,
  count: 0,
}));

/** One emoji in the quick bar or the frequently-used row — same button in
 * both places, only the width differs (the bar shares one row, the row is a
 * grid cell). */
function FrequentEmojiButton({
  item,
  onPick,
  className,
}: {
  item: EmojiUse;
  onPick: (emoji: string, url: string | null) => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={() => onPick(item.emoji, item.url)}
      className={cn(
        "aspect-square rounded-lg hover:bg-muted flex items-center justify-center transition-colors",
        className
      )}
    >
      <Emoji emoji={item.emoji} url={item.url} size="lg" />
    </button>
  );
}

interface EmojiReactionPickerProps {
  /** Called with the value to react with — a literal unicode character, or
   * a bare custom-emoji shortcode (no colons) — and its display URL when
   * it's a custom emoji. */
  onSelect: (emoji: string, customEmojiUrl?: string | null) => void;
  children: React.ReactNode;
  hoverMode?: boolean;
  /** Fires the default quick reaction on a plain click in hoverMode (the full picker opens on hover instead). */
  onQuickLike?: () => void;
  /** Custom-emoji source for the library below the frequently-used row — pass exactly one. */
  groupId?: number;
  conversationId?: number;
}

/**
 * The reaction trigger + popover used everywhere a post/comment/DM message
 * can be reacted to (replaces the old fixed-six-icon ReactionPicker and the
 * DM-only multi-panel DmReactionPicker): a frequently-used row, a search
 * box, then one flat library of standard + relevant custom emoji — no
 * per-group sub-pickers to click through.
 */
export function EmojiReactionPicker({
  onSelect,
  children,
  hoverMode,
  onQuickLike,
  groupId,
  conversationId,
}: EmojiReactionPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [openAbove, setOpenAbove] = useState(true);
  const [horizontalOffset, setHorizontalOffset] = useState(0);
  // How tall the scrolling emoji grid may be — the full GRID_HEIGHT wherever
  // there is room, less where the container is short.
  const [gridHeight, setGridHeight] = useState(GRID_HEIGHT);
  // Decided once, when the picker opens: below the `sm` breakpoint it's a
  // near-full-height bottom sheet instead of the small anchored popover, and
  // its search box skips autoFocus there so opening it doesn't immediately
  // summon the mobile keyboard.
  const [isMobile, setIsMobile] = useState(false);
  // Starts collapsed to a short quick-reaction bar every time the picker
  // opens; the "+" button in that bar expands to the full search/grid panel.
  const [expanded, setExpanded] = useState(false);
  // Clearing the search sits on the close path rather than in an effect
  // watching `open`: `open` is local state, so closing always routes through
  // one of these three call sites.
  const close = useCallback(() => {
    setOpen(false);
    setSearch("");
    setExpanded(false);
  }, []);

  const ref = useDismissable<HTMLDivElement>(open, close, { escape: false });
  const hoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Single-group mode (posts/comments/lightbox): that group's own custom emoji.
  const groupEmojisQuery = useQuery({
    queryKey: customEmojisKey(groupId),
    queryFn: () => getCustomEmojis(groupId!),
    enabled: open && groupId != null,
  });

  // DM mode: only groups every participant in THIS conversation actually
  // shares — `sharedGroups` is the union of the groups the viewer shares with
  // any ONE participant (the details pane needs that to list each participant
  // separately), so a group only one of several people in a group DM belongs
  // to would otherwise put its emoji in front of everyone else in the room.
  // The server enforces the same intersection when it resolves a shortcode
  // (`dmsRepo.sharedCustomEmoji`, OSO-513); this filter is what keeps the
  // picker from offering something the server would then leave literal.
  //
  // ONE READ, NOT TWO: `/details` is folded into the conversation document
  // (§2.14), and this is the same cache entry the open thread already holds,
  // so the picker costs nothing where it is actually used.
  const { data: conversation } = useConversation(conversationId ?? 0);
  const otherParticipantCount = conversation
    ? otherParticipants(conversation.participants).length
    : undefined;
  const commonGroups = (conversation?.sharedGroups ?? []).filter(
    (g) => g.members.length === otherParticipantCount
  );
  const dmGroupIds =
    otherParticipantCount == null ? [] : commonGroups.map((g) => g.group.id);
  const dmEmojiQueries = useQueries({
    queries: dmGroupIds.map((id) => ({
      queryKey: customEmojisKey(id),
      queryFn: () => getCustomEmojis(id),
      enabled: open,
    })),
  });

  const customEmojis =
    groupId != null
      ? (groupEmojisQuery.data?.items ?? [])
      : dmGroupIds.flatMap((id, i) => {
          const groupName = commonGroups.find((g) => g.group.id === id)?.group.name;
          return (dmEmojiQueries[i]?.data?.items ?? []).map((e) => ({ ...e, groupName }));
        });

  /**
   * The frequency table records what the viewer reacted with ANYWHERE, and
   * `?groups=` is what decides which of those may come back.
   *
   * IT IS THE SAME OSO-389 SCOPE, ON THE ROUTE THAT ENFORCES IT. `GET
   * /me/emoji-frequencies` takes the groups whose CUSTOM emoji may appear;
   * omitting it means standard emoji only — which is exactly what the DM
   * picker wants before its shared groups have resolved, and exactly wrong
   * everywhere else. Sending no groups at all is why a group that reacts with
   * nothing but its own `:tak:` was shown six generic emoji instead.
   *
   * The scope is the same set the picker is SHOWING, so the client-side filter
   * below is now defence in depth rather than the only thing holding the row
   * to this group.
   */
  const frequencyScope = groupId != null ? [groupId] : dmGroupIds;
  const frequentQuery = useQuery({
    // The scope is part of the answer, so it is part of the key: the group
    // picker and a DM picker must not share one cached row.
    queryKey: ["emoji-frequencies", frequencyScope],
    queryFn: () => getEmojiFrequencies(frequencyScope),
    enabled: open,
    staleTime: 60 * 1000,
  });

  // Defence in depth over the server-side scoping above: the response is
  // still filtered against the emoji this picker is actually showing.
  // `customEmojis` is rebuilt on every render (it's assembled from several
  // query results), so key the memo on the URL list rather than the array.
  const customEmojiUrlKey = customEmojis.map((e) => e.url).join("\n");
  const customEmojiUrls = useMemo(
    () => new Set(customEmojiUrlKey.split("\n").filter(Boolean)),
    [customEmojiUrlKey]
  );
  const frequentEmojis = frequentQuery.data?.items;
  // Falling back to the defaults happens *after* filtering: a frequent row
  // that the filter empties out would otherwise leave the quick bar with
  // nothing in it but the "+".
  const frequent = useMemo(() => {
    const filtered = (frequentEmojis ?? []).filter(
      (item) => item.url == null || customEmojiUrls.has(item.url)
    );
    return filtered.length ? filtered : DEFAULT_QUICK_EMOJI;
  }, [frequentEmojis, customEmojiUrls]);
  // The quick-reaction bar shown before the picker expands: a handful of the
  // frequent row, not however many top_for(limit: 12) returned.
  const quickEmoji = useMemo(() => frequent.slice(0, 6), [frequent]);

  // Re-measured when the picker expands, not just when it opens: it opens as
  // the short quick bar and grows to the full panel on "+", and a direction
  // picked for ~50px of popover can leave the expanded panel hanging out of
  // a tight container (the chat dock, the lightbox sidebar).
  //
  // WHICH BOX IT IS MEASURED AGAINST IS THE WHOLE POINT (OSO-560). The
  // popover is `position: absolute` inside the trigger, so it cannot escape a
  // clipping ancestor — and in the chat dock the window has room above the
  // message while the dock does not, which is how it opened upward and hung
  // across the dock's own header with its top row of emoji unreachable.
  // `popoverBoundsRect` returns that ancestor when one is marked and the
  // viewport when none is, so the feed is bounded by the window as before.
  //
  // The panel also SHRINKS: in a box too short for it on either side, the
  // emoji grid gets the room that is actually there and scrolls inside it,
  // rather than a full-height panel being placed on the roomier side and
  // clipped anyway.
  useEffect(() => {
    if (!open || !ref.current) return;

    const anchor = ref.current.getBoundingClientRect();
    const panelHeight = (expanded ? MENU_HEIGHT : QUICK_BAR_HEIGHT) + POPOVER_GAP;
    const { placement, maxHeight, left } = computeAnchoredPosition({
      anchor,
      viewport: popoverBoundsRect(ref.current),
      preferred: "above",
      desiredHeight: panelHeight,
      minHeight: QUICK_BAR_HEIGHT + POPOVER_GAP,
      align: "center",
      width: MENU_WIDTH,
    });

    setOpenAbove(placement === "above");
    // Only meaningful for the expanded panel, and the effect re-runs when it
    // expands — measuring the quick bar's own 56px would cap the grid at the
    // floor for the frame before that.
    if (expanded) {
      setGridHeight(
        Math.max(
          MIN_GRID_HEIGHT,
          Math.min(GRID_HEIGHT, maxHeight - POPOVER_GAP - PANEL_CHROME)
        )
      );
    }

    // The popover is centred on the trigger by `translateX(-50%)`; the offset
    // is how far the clamped left edge ended up from that centre.
    setHorizontalOffset(left - (anchor.left + anchor.width / 2 - MENU_WIDTH / 2));
  }, [open, expanded]);

  useEffect(() => {
    return () => {
      if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    };
  }, []);

  // Decided at the moment the picker actually opens (a real event handler,
  // not the positioning effect below — that one only runs once the popover
  // has mounted and can be measured, which is too late to decide the mode
  // that governs which markup mounts in the first place).
  const openPicker = useCallback(() => {
    // (jsdom has no matchMedia; assume desktop when we can't tell.)
    setIsMobile(
      typeof window.matchMedia === "function" &&
        window.matchMedia("(max-width: 639px)").matches
    );
    setOpen(true);
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (!hoverMode) return;
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(openPicker, 300);
  }, [hoverMode, openPicker]);

  const handleMouseLeave = useCallback(() => {
    if (!hoverMode) return;
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => {
      close();
    }, 200);
  }, [hoverMode, close]);

  const handleClick = useCallback(() => {
    if (hoverMode && onQuickLike) {
      onQuickLike();
    } else if (open) {
      close();
    } else {
      openPicker();
    }
  }, [hoverMode, onQuickLike, open, close, openPicker]);

  const pick = (emoji: string, url: string | null = null) => {
    onSelect(emoji, url);
    close();
  };

  const translateX = `calc(-50% + ${horizontalOffset}px)`;

  // Collapsed state (the default on open): a short row of common reactions
  // plus a "+" that expands to the full search/grid panel below.
  const quickBar = (
    <div className="p-2 flex items-center gap-0.5">
      {quickEmoji.map((item, i) => (
        <FrequentEmojiButton key={`${item.emoji}-${i}`} item={item} onPick={pick} className="flex-1" />
      ))}
      <button
        type="button"
        onClick={() => setExpanded(true)}
        aria-label="Vis alle emojis"
        title="Vis alle emojis"
        className="flex-1 aspect-square rounded-lg hover:bg-muted flex items-center justify-center text-lg font-medium text-subtle-foreground transition-colors"
      >
        +
      </button>
    </div>
  );

  const panel = (
    <>
      <div
        data-testid="frequent-row"
        className={`p-2 border-b border-border grid gap-0.5 ${isMobile ? "grid-cols-8" : "grid-cols-6"}`}
      >
        {frequent.map((item, i) => (
          <FrequentEmojiButton key={`${item.emoji}-${i}`} item={item} onPick={pick} className="w-full" />
        ))}
      </div>

      <div className="p-2 border-b border-border">
        <Input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Søg emoji..."
          autoFocus={!isMobile}
          className="w-full bg-surface-2 border border-border rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
      </div>

      <div
        className={isMobile ? "p-2" : "overflow-y-auto p-2"}
        style={isMobile ? undefined : { maxHeight: gridHeight }}
      >
        <EmojiGrid
          customEmojis={customEmojis}
          search={search}
          onSelectStandard={(emoji) => pick(emoji.char)}
          onSelectCustom={(emoji) => pick(emoji.shortcode, emoji.url)}
          customSectionLabel={conversationId != null ? "Gruppeemojis" : undefined}
          columns={isMobile ? 8 : 6}
        />
      </div>
    </>
  );

  return (
    <div
      className="relative"
      ref={ref}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div onClick={handleClick}>{children}</div>
      {open && (
        isMobile ? (
          <EmojiPickerSheet onClose={close} label="Vælg reaktion">
            {expanded ? panel : quickBar}
          </EmojiPickerSheet>
        ) : (
          <div
            className={`absolute left-1/2 z-50 w-60 ${openAbove ? "bottom-full mb-3" : "top-full mt-3"}`}
            style={{ transform: `translateX(${translateX})` }}
          >
            {!openAbove && (
              <div className="flex justify-center">
                <div className="w-3 h-3 bg-surface border-t border-l border-border rotate-45 mb-[-7px] shadow-sm" />
              </div>
            )}
            <div className="bg-surface rounded-xl shadow-lg border border-border overflow-hidden">
              {expanded ? panel : quickBar}
            </div>
            {openAbove && (
              <div className="flex justify-center">
                <div className="w-3 h-3 bg-surface border-b border-r border-border rotate-45 -mt-[7px] shadow-sm" />
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
}
